import { useEffect, useState } from "react";
import { api, extractError } from "@/lib/api";
import { toast } from "sonner";
import { useAuth, ROLE_LABELS, ROLES } from "@/contexts/AuthContext";
import { Plus, Pencil, Trash2, UserRound } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";

const empty = { email: "", password: "", name: "", role: "vistoriador", agency_id: "", city_ids: [], phone: "", is_active: true };

export default function Users() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [cities, setCities] = useState([]);
  const [agencies, setAgencies] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const [roleFilter, setRoleFilter] = useState("");

  const load = async () => {
    const [u, c, a] = await Promise.all([api.get("/users"), api.get("/cities"), api.get("/agencies")]);
    setItems(u.data); setCities(c.data); setAgencies(a.data);
  };
  useEffect(() => { load(); }, []);

  const openNew = () => {
    const defaults = { ...empty };
    if (user.role === "imobiliaria_admin") { defaults.role = "imobiliaria_user"; defaults.agency_id = user.agency_id; }
    setEditing(null); setForm(defaults); setOpen(true);
  };
  const openEdit = (u) => { setEditing(u); setForm({ ...empty, ...u, password: "", city_ids: u.city_ids || [] }); setOpen(true); };

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...form };
      if (editing) {
        if (!payload.password) delete payload.password;
        await api.patch(`/users/${editing.id}`, payload);
      } else {
        await api.post("/users", payload);
      }
      toast.success(editing ? "Usuário atualizado" : "Usuário criado");
      setOpen(false); load();
    } catch (e) { toast.error(extractError(e)); }
    finally { setSaving(false); }
  };

  const del = async (u) => {
    if (!window.confirm(`Excluir o usuário ${u.name}?`)) return;
    try { await api.delete(`/users/${u.id}`); toast.success("Usuário excluído"); load(); }
    catch (e) { toast.error(extractError(e)); }
  };

  const toggleCity = (id) => {
    setForm((f) => ({ ...f, city_ids: f.city_ids.includes(id) ? f.city_ids.filter((x) => x !== id) : [...f.city_ids, id] }));
  };

  const allowedRoles = user.role === "admin_geral"
    ? ROLES
    : user.role === "administrativo"
      ? ["vistoriador","supervisor","imobiliaria_admin","imobiliaria_user"]
      : ["imobiliaria_user"];

  const filtered = items.filter((u) => !roleFilter || u.role === roleFilter);
  const agName = (id) => agencies.find((a) => a.id === id)?.trade_name || "—";

  const chip = (active) => `px-3 py-1.5 text-xs border rounded transition-colors ${active ? "bg-[#0B1F3A] border-[#0B1F3A] text-[#FFD400] font-semibold" : "bg-white border-[#E5E7EB] text-[#6B7280] hover:border-[#0B1F3A]"}`;
  const selectCls = "w-full bg-white border border-[#E5E7EB] text-[#0A0A0A] text-sm rounded px-3 py-2 focus:border-[#0B1F3A] focus:outline-none";

  return (
    <div className="space-y-6" data-testid="users-page">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="inline-block text-[10px] uppercase tracking-[0.3em] font-bold px-2 py-0.5 bg-[#FFD400] text-[#0B1F3A] rounded-sm">Controle de acesso</div>
          <h1 className="font-display text-3xl sm:text-4xl font-black tracking-tighter mt-2 text-[#0B1F3A]">Usuários</h1>
          <p className="text-sm text-[#6B7280] mt-1">Perfis e permissões multi-tenant.</p>
        </div>
        <Button onClick={openNew} className="bg-[#0B1F3A] hover:bg-[#081A32] text-white" data-testid="users-new-btn">
          <Plus size={16} className="mr-2" /> Novo usuário
        </Button>
      </div>

      <div className="flex gap-3">
        <select value={roleFilter} onChange={(e) => setRoleFilter(e.target.value)}
          className="bg-white border border-[#E5E7EB] text-[#0A0A0A] text-xs rounded px-3 py-2 focus:border-[#0B1F3A] focus:outline-none"
          data-testid="users-role-filter">
          <option value="">Todos os perfis</option>
          {ROLES.map((r) => <option key={r} value={r}>{ROLE_LABELS[r]}</option>)}
        </select>
      </div>

      <div className="border border-[#E5E7EB] bg-white rounded-md overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-[10px] uppercase tracking-[0.2em] text-[#6B7280] border-b border-[#E5E7EB] bg-[#F9FAFB]">
              <th className="px-4 py-3 font-bold">Nome</th>
              <th className="px-4 py-3 font-bold">E-mail</th>
              <th className="px-4 py-3 font-bold">Perfil</th>
              <th className="px-4 py-3 font-bold">Imobiliária</th>
              <th className="px-4 py-3 font-bold">Status</th>
              <th className="px-4 py-3 font-bold text-right">Ações</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((u) => (
              <tr key={u.id} className="border-b border-[#F1F2F4] hover:bg-[#F9FAFB]" data-testid={`user-row-${u.id}`}>
                <td className="px-4 py-3 flex items-center gap-2 text-[#0A0A0A] font-medium"><UserRound size={14} className="text-[#0B1F3A]" />{u.name}</td>
                <td className="px-4 py-3 font-mono-data text-xs text-[#0A0A0A]">{u.email}</td>
                <td className="px-4 py-3 text-xs text-[#0A0A0A]">{ROLE_LABELS[u.role] || u.role}</td>
                <td className="px-4 py-3 text-xs text-[#6B7280]">{u.agency_id ? agName(u.agency_id) : "—"}</td>
                <td className="px-4 py-3">
                  <span className={`text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 rounded-sm ${u.is_active ? "bg-emerald-50 text-emerald-700" : "bg-neutral-100 text-neutral-500"}`}>
                    {u.is_active ? "Ativo" : "Inativo"}
                  </span>
                </td>
                <td className="px-4 py-3 text-right">
                  <button onClick={() => openEdit(u)} className="text-[#6B7280] hover:text-[#0B1F3A] mr-3" data-testid={`user-edit-${u.id}`}><Pencil size={14} /></button>
                  {user.role === "admin_geral" && u.id !== user.id && <button onClick={() => del(u)} className="text-[#6B7280] hover:text-red-600" data-testid={`user-delete-${u.id}`}><Trash2 size={14} /></button>}
                </td>
              </tr>
            ))}
            {filtered.length === 0 && <tr><td colSpan={6} className="px-4 py-8 text-center text-[#9CA3AF] text-sm">Nenhum usuário.</td></tr>}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-white border-[#E5E7EB] text-[#0A0A0A] max-w-2xl">
          <DialogHeader>
            <DialogTitle className="font-display text-[#0B1F3A]">{editing ? "Editar usuário" : "Novo usuário"}</DialogTitle>
            <DialogDescription className="text-[#6B7280]">Defina perfil, imobiliária e cidades autorizadas para este usuário.</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div><Label>Nome</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="user-form-name" /></div>
            <div><Label>E-mail</Label><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} disabled={!!editing} data-testid="user-form-email" /></div>
            <div>
              <Label>Perfil</Label>
              <select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })}
                disabled={editing && user.role !== "admin_geral"}
                className={selectCls}
                data-testid="user-form-role">
                {allowedRoles.map((r) => <option key={r} value={r}>{ROLE_LABELS[r]}</option>)}
              </select>
            </div>
            <div>
              <Label>Imobiliária</Label>
              <select value={form.agency_id || ""} onChange={(e) => setForm({ ...form, agency_id: e.target.value || null })}
                disabled={user.role === "imobiliaria_admin"}
                className={selectCls}>
                <option value="">— nenhuma —</option>
                {agencies.map((a) => <option key={a.id} value={a.id}>{a.trade_name}</option>)}
              </select>
            </div>
            <div><Label>Telefone</Label><Input value={form.phone || ""} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></div>
            <div>
              <Label>{editing ? "Nova senha (deixe vazio para manter)" : "Senha"}</Label>
              <Input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="user-form-password" />
            </div>
            <div className="sm:col-span-2">
              <Label>Cidades autorizadas</Label>
              <div className="flex gap-2 mt-2 flex-wrap">
                {cities.map((c) => (
                  <button type="button" key={c.id} onClick={() => toggleCity(c.id)}
                    className={chip(form.city_ids.includes(c.id))}
                  >{c.name}/{c.state}</button>
                ))}
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Switch checked={form.is_active} onCheckedChange={(v) => setForm({ ...form, is_active: v })} />
              <Label>Ativo</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} className="border-[#E5E7EB] text-[#0A0A0A] hover:bg-[#F5F6F8]">Cancelar</Button>
            <Button onClick={save} disabled={saving} className="bg-[#0B1F3A] hover:bg-[#081A32] text-white" data-testid="user-form-save">{saving ? "Salvando…" : "Salvar"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
